import { useState, useEffect, useCallback } from 'react';
import { useIsMounted } from './useIsMounted';

interface UseRecaptchaReturn {
  recaptchaLoaded: boolean;
  recaptchaError: boolean;
  getToken: (action: string) => Promise<string | null>;
}

// reCAPTCHA v3 client. Loads the script once, exposes `getToken` which the
// caller passes to whichever mailing action is firing. The shared handler
// rejects the request if the token's score is below 0.5.
export const useRecaptcha = (enabled: boolean = true): UseRecaptchaReturn => {
  // grecaptcha is external state: derive it on each render instead of
  // syncing it to a useState from a useEffect. `scriptLoaded` only covers
  // the case "we loaded the script and it fired onload" — the rest is
  // derived from the global's presence.
  const mounted = useIsMounted();
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [recaptchaError, setRecaptchaError] = useState(false);

  const recaptchaLoaded =
    scriptLoaded ||
    (mounted && typeof window !== 'undefined' && !!window.grecaptcha);

  useEffect(() => {
    if (!enabled) return;

    const siteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;
    if (!siteKey) {
      console.warn('NEXT_PUBLIC_RECAPTCHA_SITE_KEY no está configurada');
      return;
    }

    if (typeof window !== 'undefined' && window.grecaptcha) return;

    if (typeof window !== 'undefined') {
      const existingScript = document.querySelector(
        `script[src*="recaptcha/api.js"]`,
      );
      if (existingScript) return;
    }

    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      if (
        typeof window !== 'undefined' &&
        window.grecaptcha &&
        typeof window.grecaptcha.ready === 'function'
      ) {
        window.grecaptcha.ready(() => {
          setScriptLoaded(true);
        });
      } else {
        console.error('reCAPTCHA script cargado pero grecaptcha no está disponible');
        setRecaptchaError(true);
      }
    };
    script.onerror = () => {
      console.error('Error al cargar reCAPTCHA');
      setRecaptchaError(true);
    };
    document.head.appendChild(script);
  }, [enabled]);

  const getToken = useCallback(
    async (action: string): Promise<string | null> => {
      if (!recaptchaLoaded || !enabled || recaptchaError) return null;

      try {
        const siteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;
        if (
          siteKey &&
          typeof window !== 'undefined' &&
          window.grecaptcha &&
          typeof window.grecaptcha.ready === 'function'
        ) {
          const token = await new Promise<string>((resolve, reject) => {
            window.grecaptcha.ready(() => {
              window.grecaptcha
                .execute(siteKey, { action })
                .then(resolve)
                .catch(reject);
            });
          });
          return token;
        }
      } catch (error) {
        console.error('Error al obtener token de reCAPTCHA:', error);
        setRecaptchaError(true);
      }

      return null;
    },
    [recaptchaLoaded, enabled, recaptchaError],
  );

  return { recaptchaLoaded, recaptchaError, getToken };
};
